import { useState } from "react";
import { cn } from "@/utils/cn";
import { BattleEncounter } from "./gameTypes";
import { formatDate } from "./gameUtils";
import { fetchOrcamentoAnaliseSolicitacao, OrcamentoAnaliseSolicitacaoData } from "@/services/orcamento";
import { GameBattleView } from "./GameBattleView";

interface GameBattleListProps {
  encounters: BattleEncounter[];
  startDate: string;
  endDate: string;
}

export function GameBattleList({ encounters, startDate, endDate }: GameBattleListProps) {
  const [activeBattle, setActiveBattle] = useState<{
    encounter: BattleEncounter;
    data: OrcamentoAnaliseSolicitacaoData | null;
    loading: boolean;
    error: string | null;
  } | null>(null);

  const pixelFont: React.CSSProperties = { fontFamily: "'Press Start 2P', monospace" };

  async function startBattle(encounter: BattleEncounter) {
    setActiveBattle({ encounter, data: null, loading: true, error: null });
    try {
      const data = await fetchOrcamentoAnaliseSolicitacao({
        purchaseRequestId: encounter.purchaseRequestId,
        startDate,
        endDate,
      });
      setActiveBattle({ encounter, data, loading: false, error: null });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Erro ao carregar análise.";
      setActiveBattle((prev) => prev ? { ...prev, loading: false, error: msg } : null);
    }
  }

  if (activeBattle) {
    return (
      <GameBattleView
        encounter={activeBattle.encounter}
        data={activeBattle.data}
        loading={activeBattle.loading}
        error={activeBattle.error}
        onBack={() => setActiveBattle(null)}
      />
    );
  }

  return (
    <div className="p-4 h-full overflow-y-auto">
      <div className="mb-4 text-center" style={pixelFont}>
        <span className="text-emerald-500 text-[8px] tracking-widest">
          ⚔ BATALHAS DISPONÍVEIS ⚔
        </span>
      </div>

      {encounters.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-48 gap-4">
          <div className="text-4xl">🎉</div>
          <span className="text-emerald-500 text-[8px]" style={pixelFont}>
            SEM BATALHAS PENDENTES!
          </span>
          <span className="text-gray-600 text-[9px] font-mono">
            Todas as solicitações foram aprovadas
          </span>
        </div>
      ) : (
        <div className="max-w-2xl mx-auto space-y-3">
          {encounters.map((enc) => (
            <EncounterCard
              key={enc.purchaseRequestId}
              encounter={enc}
              onBattle={() => startBattle(enc)}
              pixelFont={pixelFont}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function EncounterCard({
  encounter: enc,
  onBattle,
  pixelFont,
}: {
  encounter: BattleEncounter;
  onBattle: () => void;
  pixelFont: React.CSSProperties;
}) {
  const powerLevel = enc.maxCompetenceLevel > 0 ? enc.maxCompetenceLevel : enc.itemCount;
  const isTough = powerLevel >= 3;
  const borderColor = isTough ? "border-red-600" : "border-yellow-700";
  const bgColor = isTough ? "bg-red-950/30" : "bg-yellow-950/20";

  return (
    <div className={cn("border-2 p-4", borderColor, bgColor)}>
      <div className="flex items-start gap-4">
        {/* Enemy sprite */}
        <div
          className={cn(
            "flex-shrink-0 w-14 h-14 flex flex-col items-center justify-center border text-2xl gap-0.5",
            isTough ? "border-red-600" : "border-yellow-700"
          )}
        >
          <span>{isTough ? "👿" : "👾"}</span>
          <span className="text-[6px] font-mono" style={{ color: isTough ? "#f87171" : "#facc15" }}>
            {isTough ? "CHEFE" : "INIMIGO"}
          </span>
        </div>

        <div className="flex-1 min-w-0">
          {/* SC ID + building */}
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span
              className={cn("text-[7px]", isTough ? "text-red-400" : "text-yellow-400")}
              style={pixelFont}
            >
              SC #{enc.purchaseRequestId}
            </span>
            {isTough && (
              <span
                className="text-[6px] bg-red-700 text-white px-1 py-0.5"
                style={pixelFont}
              >
                NÍVEL ALTO
              </span>
            )}
          </div>

          <p className="text-gray-400 text-[9px] font-mono mb-1 truncate">
            {enc.buildingName || enc.buildingCode || "Obra —"}
          </p>
          <p className="text-gray-600 text-[9px] font-mono mb-2 truncate">
            {enc.costCenterName || "—"}
          </p>

          {/* Stats row */}
          <div className="flex items-center gap-4 flex-wrap mb-2">
            <div className="flex items-center gap-1">
              <span className="text-gray-600 text-[8px] font-mono">ITENS:</span>
              <span
                className={cn(
                  "text-[8px] font-bold",
                  isTough ? "text-red-400" : "text-yellow-400"
                )}
                style={pixelFont}
              >
                {enc.itemCount}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-gray-600 text-[8px] font-mono">DATA:</span>
              <span className="text-[8px] font-mono text-gray-400">
                {formatDate(enc.requestDate)}
              </span>
            </div>
          </div>

          {/* Items preview */}
          {enc.itemsPreview.length > 0 && (
            <div className="space-y-0.5 mb-3">
              {enc.itemsPreview.slice(0, 3).map((item, i) => (
                <div key={i} className="text-[8px] font-mono text-gray-600 truncate">
                  › {item}
                </div>
              ))}
              {enc.itemsPreview.length > 3 && (
                <div className="text-[8px] font-mono text-gray-700">
                  + {enc.itemsPreview.length - 3} mais…
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Battle button */}
      <div className="flex justify-end mt-3">
        <button
          onClick={onBattle}
          className={cn(
            "px-4 py-2 border-2 text-[8px] font-bold transition-all",
            "hover:brightness-125 active:scale-95",
            isTough
              ? "border-red-500 text-red-400 bg-red-950/40 hover:bg-red-900/40"
              : "border-yellow-500 text-yellow-400 bg-yellow-950/40 hover:bg-yellow-900/40"
          )}
          style={pixelFont}
        >
          ⚔ BATALHAR
        </button>
      </div>
    </div>
  );
}
